import { parseThemeBlock, type ThemeBlock } from "../shared/presentation";
import { boundedKeys, exactKeys, GameSpecValidationError, glyph, integer, record, text } from "../shared/validators";

export interface TopDownCollectSpec {
  readonly version: 1;
  readonly kind: "top-down-collect";
  readonly title: string;
  readonly seed: number;
  readonly theme: ThemeBlock;
  readonly world: {
    readonly width: number;
    readonly height: number;
    readonly timeLimitSeconds: number;
    readonly goalCollectibles: number;
    readonly hazards: number;
    readonly collectibleGlyph?: string;
    readonly hazardGlyph?: string;
  };
  readonly player: {
    readonly speed: number;
    readonly playerGlyph?: string;
  };
}

/**
 * Parses the closed top-down-collect contract: the original required
 * fields exactly as v1 shipped them, plus optional presentation fields
 * (theme.motif and per-entity glyphs) that every stored spec without them
 * parses around unchanged.
 */
export function parseTopDownCollectSpec(root: Record<string, unknown>): TopDownCollectSpec {
  exactKeys(root, ["version", "kind", "title", "seed", "theme", "world", "player"], "game spec");
  if (root.kind !== "top-down-collect") throw new GameSpecValidationError("unsupported game kind");

  const world = record(root.world, "world");
  boundedKeys(world, ["width", "height", "timeLimitSeconds", "goalCollectibles", "hazards"], ["collectibleGlyph", "hazardGlyph"], "world");
  const player = record(root.player, "player");
  boundedKeys(player, ["speed"], ["playerGlyph"], "player");

  const collectibleGlyph = glyph(world.collectibleGlyph, "world.collectibleGlyph");
  const hazardGlyph = glyph(world.hazardGlyph, "world.hazardGlyph");
  const playerGlyph = glyph(player.playerGlyph, "player.playerGlyph");

  return {
    version: 1,
    kind: "top-down-collect",
    title: text(root.title, "title", 1, 80),
    seed: integer(root.seed, "seed", 0, 4_294_967_295),
    theme: parseThemeBlock(root.theme),
    world: {
      width: integer(world.width, "world.width", 320, 1_920),
      height: integer(world.height, "world.height", 240, 1_080),
      timeLimitSeconds: integer(world.timeLimitSeconds, "world.timeLimitSeconds", 15, 300),
      goalCollectibles: integer(world.goalCollectibles, "world.goalCollectibles", 1, 50),
      hazards: integer(world.hazards, "world.hazards", 0, 50),
      ...(collectibleGlyph === undefined ? {} : { collectibleGlyph }),
      ...(hazardGlyph === undefined ? {} : { hazardGlyph }),
    },
    player: {
      speed: integer(player.speed, "player.speed", 100, 500),
      ...(playerGlyph === undefined ? {} : { playerGlyph }),
    },
  };
}
